import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Lamborghini Countach (LP5000 QV, ~1985) — the most extreme low-wide-long
// wedge at true scale (1 unit = 1 metre): length ~4.3, width ~2.0 (extremely
// WIDE), height ~0.95 (extremely LOW — barely above the wheels), wheelbase
// ~2.5, ~0.32 m wheels. Nose is +Z. Authored for the `cars` showcase batch.
//
// The silhouette is ONE unbroken straight wedge — a doorstop, never a three-box
// and never a pickup: the hood and the near-flat windscreen ride the SAME
// shallow rake, so a very low sharp pointed nose almost at the ground climbs in
// a single straight line to a low flat roof cap; the dark glass is a thin
// horizontal slot, not a tall box. Behind the cabin a slatted rear-window panel
// and a short slatted engine deck fall to a sharp vertical Kamm tail. The rear
// haunches flare dramatically WIDER than the narrow greenhouse (wide hips), fed
// by low angular intake scoops on the flanks ahead of the fat rear wheels. Flat
// rectangular pop-up headlamps lie flush in the low nose; a modest QV wing
// stands on two uprights over the deck; the wheels are the bright five-hole
// "telephone-dial" alloys. Body panels carry the "paint" recolour slot
// (Countach red); `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#c81a1a"; // classic Countach red — the "paint" slot default
const TRIM = "#141518"; // gloss black (slats, pillars, wing uprights)
const CLAD = "#202225"; // lower valances, splitter, rocker sills
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side glazing (the thin slot)
const SCREEN = "#24303b"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // intake mouths + engine grille mesh (near-black)
const SPOKE = "#d7dce1"; // bright telephone-dial alloy face
const DISH = "#101114"; // dark wheel dish + dial holes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // pop-up headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#c41726"; // round tail lamps
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The car is LOW, WIDE and LONG — the belt line
// sits at ~0.70 (just above the 0.64 wheel tops) and the roof caps at only
// ~0.95, while the body runs ~1.8 up front and flares to a full ~2.0 over the
// rear hips, so every square view reads emphatically wider than tall (~2:1).
const WHEEL_R = 0.32;
const AXLE_F = 1.28;
const AXLE_R = -1.24; // wheelbase ~2.5
const WHEEL_X_F = 0.82; // half track, front — narrower pointed front
const WHEEL_X_R = 0.86; // half track, rear (fat tyres under the wide hips)

// The single wedge rake: hood and windscreen share this slope, so nose -> hood
// -> windscreen -> roof front are one straight climbing line (no vertical wall).
const WEDGE_RAKE = 0.223;
const REARWIN_SLOPE = 0.427; // rear-window slat panel falling toward the tail
const DECK_SLOPE = 0.1; // short slatted engine deck, gently falling to the Kamm tail

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One clean "telephone-dial" alloy: tyre, brake disc, a bright dial face with
 *  five dark round holes, a chrome centre cap. Every part shares `name` so the
 *  `drive` track spins them as one node about the axle. `side` is +1 for the
 *  right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.27, 0.27, 0.05, 22, SPOKE, [side * (outer - 0.03), y, z], [0, 0, Math.PI / 2], { name }), // bright dial face
  ];
  for (let k = 0; k < 5; k++) {
    const ang = (k * 2 * Math.PI) / 5;
    parts.push(
      cyl(0.058, 0.058, 0.04, 12, DISH, // dark round dial hole, proud of the face
        [side * (outer - 0.015), y + 0.155 * Math.cos(ang), z + 0.155 * Math.sin(ang)],
        [0, 0, Math.PI / 2], { name }),
    );
  }
  parts.push(cyl(0.06, 0.06, 0.03, 12, CHROME, [side * (outer + 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the nose intake; rear pair sits beside the tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.62, 0.32, 2.08], size: [0.14, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.62, 0.32, 2.08], size: [0.14, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.78, 0.53, -2.02], size: [0.1, 0.06, 0.03] },
  { name: "indicator-rr", pos: [0.78, 0.53, -2.02], size: [0.1, 0.06, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (wide track — fat tyres planted at the corners) -----------
  P.push(
    ...wheel(-1, WHEEL_X_F, 0.28, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, 0.28, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, 0.34, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, 0.34, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.93, 0.42, 0.36, 0.04)); // front arch (low flat brow)
    P.push(...arch(s, AXLE_R, 1.0, 0.46, 0.4, 0.05)); // flared rear arch, clearly wider
  }
  P.push(box(1.6, 0.14, 3.6, TRIM, [0, 0.19, -0.05])); // underbody pan (fills under the wedge)

  // -- Lower body: a very low WIDE tub, belt at ~0.70 -------------------------
  P.push(paint(1.8, 0.48, 3.5, [0, 0.46, -0.05])); // main tub, sill to belt line

  // Low pointed WIDE wedge nose: a very low front face, a long flat hood on the
  // wedge rake, flat fender crests beside it. The hood line is unbroken.
  P.push(paint(1.72, 0.16, 0.12, [0, 0.34, 2.1])); // very low wide front prow face
  P.push(paint(1.5, 0.06, 1.35, [0, 0.5, 1.42], [WEDGE_RAKE, 0, 0])); // long flat hood on the wedge rake
  for (const s of S) P.push(paint(0.22, 0.08, 1.35, [s * 0.8, 0.53, 1.42], [WEDGE_RAKE, 0, 0])); // flat fender crests
  P.push(box(1.7, 0.1, 0.14, CLAD, [0, 0.18, 2.06])); // low wide front chin splitter

  // -- Rear hips: the widest, most muscular masses; flare well past the cabin --
  // The slatted engine cover drops into the channel between them (never a bed).
  for (const s of S) {
    P.push(paint(0.54, 0.5, 1.8, [s * 0.71, 0.48, -0.95])); // hip body, outer edge at 0.98
    // low buttress fillet closing the void from the cabin side onto the hip top
    P.push(paint(0.2, 0.2, 0.7, [s * 0.53, 0.63, -0.5], [-0.12, 0, 0]));
  }

  // -- Engine bay + slatted cover, dropped flush into the deck ----------------
  P.push(box(1.0, 0.28, 0.95, MESH, [0, 0.55, -1.4])); // engine bay block under the cover
  P.push(box(1.02, 0.05, 0.9, TRIM, [0, 0.73, -1.42], [-DECK_SLOPE, 0, 0])); // dark cover base
  for (let i = 0; i < 7; i++) {
    const z = -1.05 - 0.11 * i; // from -1.05 back to -1.71
    const y = 0.76 + 0.1 * (z + 0.98) + 0.03; // ride the falling slope
    P.push(box(0.96, 0.02, 0.05, MESH, [0, y, z], [-DECK_SLOPE, 0, 0])); // engine-cover slats
  }

  // -- Greenhouse: a THIN glass slot under a low flat roof cap ----------------
  // The windscreen shares the hood's rake, so it continues the single wedge
  // line rather than standing as a vertical wall. A narrow greenhouse lets the
  // wide body and hips dominate every square view.
  P.push(box(1.2, 0.05, 1.0, SCREEN, [0, 0.77, 0.32], [WEDGE_RAKE, 0, 0])); // near-flat windscreen on the wedge rake
  for (const s of S) P.push(paint(0.07, 0.07, 1.0, [s * 0.56, 0.77, 0.32], [WEDGE_RAKE, 0, 0])); // thin A-pillar rails
  P.push(paint(1.0, 0.05, 0.64, [0, 0.88, -0.46])); // low flat roof cap
  P.push(box(0.94, 0.2, 0.95, CABIN, [0, 0.65, -0.1])); // dark interior block under the slot
  for (const s of S) {
    P.push(box(0.03, 0.16, 0.85, GLASS, [s * 0.5, 0.76, -0.1], [0, 0, s * 0.06])); // thin side-glass slot
    P.push(box(0.05, 0.16, 0.06, TRIM, [s * 0.5, 0.76, -0.5], [0, 0, s * 0.06])); // B-pillar
  }
  // rear-window slat panel bridges the roof down to the deck as one falling face
  P.push(box(0.92, 0.05, 0.34, TRIM, [0, 0.83, -0.89], [-REARWIN_SLOPE, 0, 0])); // slat panel base
  for (let i = 0; i < 3; i++) {
    const z = -0.82 - 0.09 * i;
    const y = 0.88 + 0.4545 * (z + 0.78) + 0.03;
    P.push(box(0.86, 0.02, 0.05, MESH, [0, y, z], [-REARWIN_SLOPE, 0, 0])); // rear-window louvres
  }

  // -- Rear wing: a thin blade on two slim uprights, in clear air over the deck
  //    (the modest QV cue) -----------------------------------------------------
  for (const s of S) P.push(box(0.05, 0.16, 0.1, TRIM, [s * 0.5, 0.82, -1.82])); // slim uprights
  P.push(paint(1.4, 0.04, 0.32, [0, 0.92, -1.83])); // thin wing blade
  P.push(box(1.4, 0.02, 0.05, TRIM, [0, 0.91, -1.68])); // blade leading edge
  for (const s of S) P.push(box(0.025, 0.11, 0.32, TRIM, [s * 0.68, 0.91, -1.83])); // end plates

  // -- Low angular flank scoops (dropped onto the flanks ahead of rear wheels) -
  for (const s of S) {
    P.push(box(0.05, 0.22, 0.55, MESH, [s * 0.98, 0.5, -0.5])); // low side intake mouth
    P.push(paint(0.06, 0.05, 0.6, [s * 0.96, 0.62, -0.5])); // scoop upper lip
    P.push(paint(0.06, 0.05, 0.6, [s * 0.96, 0.38, -0.5])); // scoop lower lip
    P.push(paint(0.06, 0.24, 0.06, [s * 0.96, 0.5, -0.24])); // scoop front wall
    P.push(box(0.03, 0.05, 0.32, MESH, [s * 0.9, 0.42, 0.15])); // small NACA duct slot on the flank
    P.push(box(0.04, 0.12, 2.5, CLAD, [s * 0.9, 0.3, -0.1])); // rocker sill (reaches both arches)
  }

  // -- Front fascia: flat rectangular pop-up lamps laid flush in the low nose --
  for (const s of S) {
    P.push(box(0.36, 0.05, 0.16, TRIM, [s * 0.46, 0.43, 1.96], [WEDGE_RAKE, 0, 0])); // flush pop-up housing
    P.push(box(0.32, 0.03, 0.13, LAMP, [s * 0.46, 0.45, 1.97], [WEDGE_RAKE, 0, 0])); // rectangular lamp face
  }
  P.push(box(1.42, 0.1, 0.05, MESH, [0, 0.26, 2.13])); // very low wide thin front intake
  P.push(cyl(0.04, 0.04, 0.02, 16, CHROME, [0, 0.4, 2.16], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.4, 0.09, 0.02, PLATE, [0, 0.26, 2.15])); // small low front plate

  // -- Rear fascia: sharp Kamm tail — quad round lamps, mesh grille, exhausts --
  P.push(box(1.72, 0.42, 0.08, TRIM, [0, 0.47, -2.0])); // black rear Kamm panel
  for (const s of S) {
    // two round tail lamps per side on the black panel
    P.push(cyl(0.08, 0.08, 0.05, 16, TAIL_RED, [s * 0.44, 0.52, -2.05], [Math.PI / 2, 0, 0]));
    P.push(cyl(0.08, 0.08, 0.05, 16, TAIL_RED, [s * 0.64, 0.52, -2.05], [Math.PI / 2, 0, 0]));
  }
  // central black mesh grille with vertical slats
  P.push(box(0.6, 0.22, 0.03, MESH, [0, 0.47, -2.05]));
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.18, 0.02, "#26282c", [i * 0.13, 0.47, -2.07]));
  }
  // a row of four dark gunmetal exhaust tips, centred low
  for (const dx of [-0.18, -0.06, 0.06, 0.18]) {
    P.push(cyl(0.045, 0.045, 0.09, 12, EXHAUST, [dx, 0.28, -2.07], [Math.PI / 2, 0, 0]));
  }
  P.push(box(1.5, 0.14, 0.12, CLAD, [0, 0.2, -1.98])); // rear diffuser valance
  P.push(box(0.4, 0.09, 0.02, PLATE, [-0.44, 0.4, -2.07])); // small rear plate, offset to one side

  // -- Furniture: small mirrors firmly attached to the door tops --------------
  for (const s of S) {
    P.push(box(0.12, 0.03, 0.04, TRIM, [s * 0.62, 0.64, 0.4])); // solid mirror stalk overlapping the body
    P.push(paint(0.06, 0.08, 0.09, [s * 0.72, 0.66, 0.4])); // mirror head
    P.push(box(0.012, 0.06, 0.07, GLASS, [s * 0.755, 0.66, 0.41])); // mirror glass, facing back
    P.push(box(0.02, 0.02, 0.11, CHROME, [s * 0.905, 0.5, -0.05])); // door handle
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const lamborghiniCountach = defineModel({
  id: "lamborghini-countach",
  name: "Lamborghini Countach",
  batchId: "cars",
  tags: ["car", "lamborghini", "countach", "supercar", "sports", "classic"],
  summary: "A Lamborghini Countach — the extreme low-wide-long wedge supercar at true scale, in Countach red.",
  description:
    "A mid-1980s Lamborghini Countach (LP5000 QV) at real 1 unit = 1 metre " +
    "scale (about 4.3 long, an extremely wide ~2.0, an extremely low ~0.95 " +
    "tall, ~2.5 wheelbase). The silhouette is one unbroken straight wedge — a " +
    "doorstop: the hood and the near-flat windscreen share a single shallow " +
    "rake, so a very low sharp pointed nose almost at the ground climbs in one " +
    "straight line to a low flat roof cap, with the dark glass reduced to a " +
    "thin horizontal slot. Behind the cabin a slatted rear-window panel and a " +
    "short slatted engine deck fall to a sharp vertical Kamm tail. The " +
    "signature cues are all present: flat rectangular pop-up headlamps flush " +
    "in the low nose, a very low wide front intake, rear hips flaring " +
    "dramatically wider than the narrow greenhouse fed by low angular flank " +
    "scoops ahead of the fat rear tyres, a modest QV rear wing on two uprights " +
    "over the deck, a black Kamm tail with quad round tail lamps, a mesh " +
    "engine grille and four exhausts, and bright five-hole telephone-dial " +
    "alloys. Body panels carry the 'paint' recolour slot (Countach red); " +
    "`drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.25,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
